/**
 * StandardText Form Component
 * Reusable form for creating and editing StandardText data
 *
 * A aba Editar é uma grade de pares: título inteiro, Categoria com Status,
 * escopo com Seção, e o conteúdo ao lado das variáveis. Os campos que só às
 * vezes existem (Seção e a empresa do texto) ficam no fim da linha ou dentro da
 * célula do checkbox, para que aparecer e sumir não abra buraco no meio. A
 * prévia mora numa aba própria, com a largura toda, e a empresa é escolhida na
 * própria tela: o modal bloqueante obrigava a decidir o escopo antes de ver o
 * resto do formulário.
 *
 * @version 2026.1.0
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Alert,
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  FormHelperText,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import { Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import {
  DocumentSection,
  DocumentSectionCatalog,
  DocumentVariable,
  StandardText,
  StandardTextCategory,
  StandardTextFormData,
  StandardTextStatus,
} from '../../../types/standardText';
import { standardTextService } from '../../../services/standardTextService';
import { getApiErrorMessage } from '../../../types/common';
import {
  CompanySelectField,
  EditorDeTextoRico,
  EditorDeTextoRicoHandle,
} from '../../../components/forms';
import { VariablePanel } from './VariablePanel';
import { StandardTextPreview } from './StandardTextPreview';

/**
 * Opção padrão do combo de seção: texto que vale para o documento inteiro.
 *
 * O valor é um sentinela, e não string vazia, porque o `Select` do MUI trata
 * `''` como "sem seleção" e mostraria um combo em branco no lugar do rótulo da
 * opção. Vira `sectionKey` nulo no envio.
 */
const TODAS_AS_SECOES = '__todas__';

type AbaDoFormulario = 'editar' | 'previa';

// Validation schema
const validationSchema = yup.object({
  title: yup.string().required('Título é obrigatório').min(3, 'Mínimo de 3 caracteres').max(255, 'Máximo de 255 caracteres'),
  content: yup.string().required('Conteúdo é obrigatório').min(1, 'Conteúdo não pode ser vazio'),
  category: yup.string().required('Categoria é obrigatória'),
  sectionKey: yup.string().nullable(),
  isGlobal: yup.boolean().required(),
  companyId: yup.number().nullable().when('isGlobal', {
    is: false,
    then: (schema) => schema.required('Empresa é obrigatória para textos específicos'),
    otherwise: (schema) => schema.nullable(),
  }),
  status: yup.string().oneOf(Object.values(StandardTextStatus)).required(),
});

type StandardTextFormValues = yup.InferType<typeof validationSchema>;

interface StandardTextFormProps {
  initialData?: Partial<StandardText>;
  onSubmit: (data: StandardTextFormData) => void;
  onCancel: () => void;
  isSubmitting?: boolean;
  categories?: Array<{ value: string; label: string }>;
  /**
   * Recusa que voltou do salvar — o texto que não compila chega aqui com a
   * chave nomeada pelo compilador (#412, D6). Fica junto do conteúdo, que é
   * onde o conserto acontece.
   */
  submitError?: string | null;
}

/** "PGR — Programa de Gerenciamento de Riscos" vira "PGR" no título sugerido. */
const siglaDoDocumento = (label: string): string => label.split('—')[0].trim();

export const StandardTextForm: React.FC<StandardTextFormProps> = ({
  initialData,
  onSubmit,
  onCancel,
  isSubmitting = false,
  categories = [],
  submitError = null,
}) => {
  const isEditing = Boolean(initialData?.id);

  const [aba, setAba] = useState<AbaDoFormulario>('editar');
  const [documents, setDocuments] = useState<DocumentSectionCatalog[]>([]);
  const [variables, setVariables] = useState<DocumentVariable[]>([]);
  const [erroDeSecoes, setErroDeSecoes] = useState<string | null>(null);
  const [erroDeVariaveis, setErroDeVariaveis] = useState<string | null>(null);
  // Título sugerido só é substituído enquanto ninguém o reescreveu à mão.
  const [sugestaoDeTitulo, setSugestaoDeTitulo] = useState('');
  const editorDoConteudo = useRef<EditorDeTextoRicoHandle | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      title: initialData?.title || '',
      content: initialData?.content || '',
      category: initialData?.category || '',
      sectionKey: initialData?.sectionKey ?? TODAS_AS_SECOES,
      isGlobal: initialData?.isGlobal ?? true,
      companyId: initialData?.companyId || null,
      status: initialData?.status || StandardTextStatus.ATIVO,
    },
  });

  const isGlobal = watch('isGlobal');
  const category = watch('category');
  const sectionKey = watch('sectionKey');
  const content = watch('content');
  const title = watch('title');
  const companyId = watch('companyId');

  useEffect(() => {
    standardTextService
      .getDocumentSections()
      .then((catalogo) => {
        setDocuments(catalogo);
        setErroDeSecoes(null);
      })
      // Catálogo que falha calado fica idêntico a "essa categoria não tem seções".
      .catch((err: unknown) => setErroDeSecoes(getApiErrorMessage(err, 'erro desconhecido')));
    standardTextService
      .getVariables()
      .then((catalogo) => {
        setVariables(catalogo);
        setErroDeVariaveis(null);
      })
      .catch((err: unknown) => setErroDeVariaveis(getApiErrorMessage(err, 'erro desconhecido')));
  }, []);

  // O combo de seção só existe para categoria que pertence a algum documento.
  const document = useMemo(
    () => documents.find((doc) => doc.sections.some((section) => section.category === category)),
    [documents, category]
  );

  const sections = useMemo<DocumentSection[]>(
    () =>
      (document?.sections ?? [])
        .filter((section) => section.category === category)
        .sort((a, b) => a.order - b.order),
    [document, category]
  );

  // Categoria trocada leva a seção junto: seção de outro documento não existe aqui.
  // Antes do catálogo chegar toda seção parece inválida — inclusive a que veio
  // no link da pendência, que seria apagada na primeira renderização.
  useEffect(() => {
    if (documents.length === 0) return;

    const secaoEscolhida = sectionKey && sectionKey !== TODAS_AS_SECOES;
    if (secaoEscolhida && !sections.some((section) => section.key === sectionKey)) {
      setValue('sectionKey', TODAS_AS_SECOES);
    }
  }, [documents, sections, sectionKey, setValue]);

  // Título sugerido pela seção — editável, porque o unique (title, companyId) continua valendo.
  useEffect(() => {
    if (!document || !sectionKey || sectionKey === TODAS_AS_SECOES) return;

    const section = sections.find((item) => item.key === sectionKey);
    if (!section) return;

    const suggestion = `${siglaDoDocumento(document.label)} — ${section.title}`;
    const currentTitle = getValues('title');
    if (currentTitle === '' || currentTitle === sugestaoDeTitulo) {
      setSugestaoDeTitulo(suggestion);
      setValue('title', suggestion, { shouldValidate: true });
    }
  }, [document, sections, sectionKey, setValue, getValues, sugestaoDeTitulo]);

  // Clear companyId when isGlobal becomes true
  useEffect(() => {
    if (isGlobal) {
      setValue('companyId', null);
    }
  }, [isGlobal, setValue]);

  const tituloVeioDaSugestao = sugestaoDeTitulo !== '' && title === sugestaoDeTitulo;
  const mostrarPainelDeVariaveis = variables.length > 0 || erroDeVariaveis !== null;

  // A variável entra pela API do editor, no cursor (D10): o conteúdo agora é
  // HTML, e recortar string por posição inseriria a chave dentro de uma tag.
  const insertVariable = (token: string) => {
    editorDoConteudo.current?.inserirNoCursor(`{{${token}}}`);
  };

  const submit = (values: StandardTextFormValues) => {
    const sectionKeyEfetivo =
      values.sectionKey && values.sectionKey !== TODAS_AS_SECOES ? values.sectionKey : null;

    onSubmit({
      title: values.title,
      content: values.content,
      category: values.category as StandardTextCategory,
      sectionKey: sectionKeyEfetivo,
      companyId: values.companyId ?? null,
      isGlobal: values.isGlobal,
      status: values.status as StandardTextStatus,
    });
  };

  // Campo faltando é sempre na aba Editar: enviar da Prévia não pode virar um
  // botão que não faz nada.
  const voltarParaOsCampos = () => setAba('editar');

  return (
    <Box component="form" onSubmit={handleSubmit(submit, voltarParaOsCampos)} noValidate>
      <Tabs
        value={aba}
        onChange={(_, valor: AbaDoFormulario) => setAba(valor)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab value="editar" label="Editar" />
        <Tab value="previa" label="Prévia" />
      </Tabs>

      {aba === 'editar' ? (
        <Grid container spacing={3}>
          {erroDeSecoes && (
            <Grid size={12}>
              <Alert severity="warning">
                {`Não foi possível carregar as seções dos documentos (${erroDeSecoes}). Sem o catálogo o campo Seção não aparece — recarregue a página para tentar de novo.`}
              </Alert>
            </Grid>
          )}

          {/* Title */}
          <Grid size={12}>
            <Controller
              name="title"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Título"
                  fullWidth
                  required
                  error={Boolean(errors.title)}
                  helperText={
                    errors.title?.message ??
                    (tituloVeioDaSugestao
                      ? 'Sugerido a partir da seção escolhida — pode editar à vontade.'
                      : undefined)
                  }
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {/* Category */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <FormControl fullWidth required error={Boolean(errors.category)}>
              <InputLabel id="standard-text-category-label">Categoria</InputLabel>
              <Controller
                name="category"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="standard-text-category-label"
                    label="Categoria"
                    disabled={isSubmitting}
                  >
                    {categories.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
              {errors.category && <FormHelperText>{errors.category.message}</FormHelperText>}
            </FormControl>
          </Grid>

          {/* Status */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <FormControl fullWidth required>
              <InputLabel id="standard-text-status-label">Status</InputLabel>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    labelId="standard-text-status-label"
                    label="Status"
                    disabled={isSubmitting}
                  >
                    <MenuItem value={StandardTextStatus.ATIVO}>Ativo</MenuItem>
                    <MenuItem value={StandardTextStatus.INATIVO}>Inativo</MenuItem>
                  </Select>
                )}
              />
              <FormHelperText>Texto inativo não é usado na emissão de documentos.</FormHelperText>
            </FormControl>
          </Grid>

          {/* Escopo: o seletor de empresa mora dentro da célula do checkbox, e
              não ao lado dela, para que marcar "texto global" mude só a altura
              desta célula em vez de remexer a linha inteira. */}
          <Grid
            size={{
              xs: 12,
              md: 6
            }}>
            <Controller
              name="isGlobal"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox {...field} checked={field.value} disabled={isSubmitting || isEditing} />
                  }
                  label="Texto global (vale para todas as empresas)"
                />
              )}
            />

            {!isGlobal && (
              <Controller
                name="companyId"
                control={control}
                render={({ field }) => (
                  <CompanySelectField
                    value={field.value ? String(field.value) : ''}
                    onChange={(valor) => field.onChange(valor ? Number(valor) : null)}
                    error={Boolean(errors.companyId)}
                    helperText={
                      errors.companyId?.message ?? 'A prévia resolve as variáveis com os dados dela.'
                    }
                    hideOnEdit={false}
                    isEditMode={isEditing}
                    disabled={isSubmitting || isEditing}
                    label="Empresa deste texto"
                  />
                )}
              />
            )}

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {isEditing
                ? 'O escopo e a empresa são definidos na criação e não mudam depois.'
                : 'Texto global atende qualquer empresa; um texto de empresa específica tem preferência sobre o global na hora de emitir o documento.'}
            </Typography>
          </Grid>

          {/* Seção: campo condicional no fim da linha — quando a categoria não
              tem seções a linha simplesmente acaba antes. */}
          {sections.length > 0 && (
            <Grid
              size={{
                xs: 12,
                md: 6
              }}>
              <FormControl fullWidth>
                <InputLabel id="standard-text-section-label">Seção</InputLabel>
                <Controller
                  name="sectionKey"
                  control={control}
                  render={({ field }) => (
                    <Select
                      {...field}
                      value={field.value ?? TODAS_AS_SECOES}
                      labelId="standard-text-section-label"
                      label="Seção"
                      disabled={isSubmitting}
                    >
                      <MenuItem value={TODAS_AS_SECOES}>Vale para todas as seções</MenuItem>
                      {sections.map((section) => (
                        <MenuItem key={section.key} value={section.key}>
                          {section.title}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>
                  {`Cada seção do documento pode ter texto próprio — estas são as seções de ${document?.label ?? 'documento'}. "Vale para todas as seções" serve o documento inteiro.`}
                </FormHelperText>
              </FormControl>
            </Grid>
          )}

          {/* Content */}
          <Grid
            size={{
              xs: 12,
              md: mostrarPainelDeVariaveis ? 8 : 12
            }}>
            {submitError && (
              <Alert
                severity="error"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => setAba('previa')}>
                    Ver na prévia
                  </Button>
                }
              >
                {submitError}
              </Alert>
            )}

            <Controller
              name="content"
              control={control}
              render={({ field }) => (
                <EditorDeTextoRico
                  ref={editorDoConteudo}
                  label="Conteúdo"
                  required
                  value={field.value ?? ''}
                  onChange={field.onChange}
                  textoGuia="Escreva aqui o texto desta seção."
                  error={Boolean(errors.content)}
                  helperText={
                    errors.content?.message ??
                    'Use as variáveis para o texto se adaptar a cada empresa. A aba Prévia mostra como ele fica resolvido.'
                  }
                  disabled={isSubmitting}
                />
              )}
            />
          </Grid>

          {mostrarPainelDeVariaveis && (
            <Grid
              size={{
                xs: 12,
                md: 4
              }}>
              {erroDeVariaveis ? (
                <Alert severity="warning">
                  {`Não foi possível carregar as variáveis disponíveis (${erroDeVariaveis}). Dá para escrever o texto assim mesmo e digitar as chaves à mão.`}
                </Alert>
              ) : (
                <VariablePanel
                  variables={variables}
                  onInsert={insertVariable}
                  disabled={isSubmitting}
                />
              )}
            </Grid>
          )}
        </Grid>
      ) : (
        <StandardTextPreview
          content={content ?? ''}
          isGlobal={isGlobal}
          companyId={companyId ?? null}
          sectionKey={sectionKey === TODAS_AS_SECOES ? null : sectionKey}
          onEditar={voltarParaOsCampos}
        />
      )}

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 3 }}>
        <Button
          onClick={onCancel}
          variant="outlined"
          startIcon={<CancelIcon />}
          disabled={isSubmitting}
        >
          Cancelar
        </Button>
        <Button
          type="submit"
          variant="contained"
          startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
          disabled={isSubmitting}
        >
          {isEditing ? 'Atualizar' : 'Criar'} Texto
        </Button>
      </Box>
    </Box>
  );
};

export default StandardTextForm;
